import type { Observable } from 'rxjs';
import type { Update } from '@ngrx/entity';
import type { Uuid } from '@common/types';
import type { NormalizedColumnEntity, DenormalizedColumnEntity, WebLegacyWidgetEntity } from '@web/types';

import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import { columnInstance, specificColumnInstance } from '@web/store/page-structure/columns/columns.models';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';

import ColumnsActions from './columns.actions';
import * as ColumnsSelectors from './columns.selectors';

@Injectable()
export class ColumnsFacade {
    private readonly store = inject(Store);
    private readonly widgetsFacade = inject(WidgetsFacade);

    /**
     * Combine pieces of state using createSelector,
     * and expose them as observables through the facade.
     */
    public readonly columns$ = this.store.select(ColumnsSelectors.selectAllColumns);
    public readonly columnsIds$ = this.store.select(ColumnsSelectors.selectColumnsIds);
    public readonly columnsEntities$ = this.store.select(ColumnsSelectors.selectColumnsEntities);

    /**
     * Use the initialization action to perform one
     * or more tasks in your Effects.
     */
    public init(columns: NormalizedColumnEntity[]): void {
        this.store.dispatch(ColumnsActions.init({ columns }));
    }

    public addColumn(column: DenormalizedColumnEntity, index: number): void {
        this.store.dispatch(ColumnsActions.addColumn({ column, index }));
    }

    public createColumn(sectionId: Uuid, index: number): void {
        this.store.dispatch(ColumnsActions.createColumn({ index, sectionId }));
    }

    public moveColumnToSection(
        columnId: Uuid,
        prevSectionId: Uuid,
        currentSectionId: Uuid,
        previousIndex: number,
        currentIndex: number,
        skipHistory: boolean = false
    ): void {
        this.store.dispatch(
            ColumnsActions.columnMoved({
                columnId,
                prevSectionId,
                currentSectionId,
                previousIndex,
                currentIndex,
                skipHistory,
            })
        );
    }

    public updateColumnProperties(id: Uuid, changes: Partial<NormalizedColumnEntity>): void {
        this.store.dispatch(ColumnsActions.updateColumnProperties({ update: { id, changes } }));
    }

    public updateColumnsProperties(update: Update<NormalizedColumnEntity>[]): void {
        this.store.dispatch(ColumnsActions.updateColumnsProperties({ update }));
    }

    public removeColumn(column: DenormalizedColumnEntity, index: number, skipHistory: boolean = false): void {
        this.store.dispatch(ColumnsActions.removeColumn({ column, index, skipHistory }));

        this.widgetsFacade.removeManyWidgets(column.children);
    }

    public copyColumn(column: DenormalizedColumnEntity, index: number): void {
        this.store.dispatch(ColumnsActions.copyColumn({ column, index }));
    }

    public removeManyColumns(columns: DenormalizedColumnEntity[]): void {
        this.store.dispatch(ColumnsActions.removeManyColumns({ columnsIds: columns.map((column) => column.id) }));

        const widgets: WebLegacyWidgetEntity[] = columns.reduce(
            (acc: WebLegacyWidgetEntity[], column: DenormalizedColumnEntity) => {
                acc.push(...column.children);
                return acc;
            },
            [] as WebLegacyWidgetEntity[]
        );

        this.widgetsFacade.removeManyWidgets(widgets);
    }

    public getColumnInstanceValue(sectionId: Uuid): NormalizedColumnEntity {
        return columnInstance({ parentId: sectionId });
    }

    public getSpecificColumnInstanceValue(sectionId: Uuid): NormalizedColumnEntity {
        return specificColumnInstance({ parentId: sectionId });
    }

    public selectColumnsIds(): Observable<string[]> {
        return this.store.select(ColumnsSelectors.selectColumnsIds) as Observable<string[]>;
    }

    public columnById$(id: Uuid): Observable<NormalizedColumnEntity> {
        return this.store.select(ColumnsSelectors.selectColumnById(id));
    }

    public denormalizedColumnById$(id: Uuid): Observable<DenormalizedColumnEntity> {
        return this.store.select(ColumnsSelectors.selectDenormalizedColumnById(id));
    }
}
